import React, { useMemo, useState } from 'react';
import Modal from './Modal.mjs';
import ClosingX from './ClosingX.jsx';
import BButton from './BButton.mjs';
import { METADATA_SCHEMA } from './MetadataEditor.jsx';
import * as FileService from '../services/FileService.mjs';
import { runBulk } from '../utils/bulkApply';
import { notify, reportError } from '../notifications/errorBus';
import '../css/BulkActions.css';

const CUSTOM_KEY = '__custom';

// `name` is intentionally omitted — bulk-setting the same file name across many
// files is almost always a mistake (and breaks the "name identifies a file"
// expectation). Custom keys are still allowed for anything not in the schema.
const SCHEMA_KEYS = Object.entries(METADATA_SCHEMA).filter(([key]) => key !== 'name');

function coerceValue(type, raw) {
    if (type.endsWith('[]')) {
        let arr = raw.split(',').map(s => s.trim()).filter(Boolean);
        if (type === 'number[]') arr = arr.map(Number).filter(n => !Number.isNaN(n));
        return arr;
    }
    if (type === 'number') return Number(raw);
    return raw;
}

/**
 * Set a single metadata entry (key → value) on every selected file.
 * Overwrites the field on each file; it does not merge list values.
 */
const BulkMetadataModal = ({ files, onClose, onApplied }) => {
    const [keyChoice, setKeyChoice] = useState(SCHEMA_KEYS[0]?.[0] ?? CUSTOM_KEY);
    const [customKey, setCustomKey] = useState('');
    const [value, setValue] = useState('');
    const [busy, setBusy] = useState(false);

    const isCustom = keyChoice === CUSTOM_KEY;
    const fieldSchema = useMemo(
        () => (isCustom ? { type: 'string', label: customKey || 'Custom property' } : METADATA_SCHEMA[keyChoice]),
        [isCustom, keyChoice, customKey]
    );

    const finalKey = isCustom ? customKey.trim().replace(/\s+/g, '_') : keyChoice;
    const isList = fieldSchema.type.endsWith('[]');
    const canSubmit = finalKey.length > 0 && value.trim().length > 0 && !busy;

    const handleApply = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            const finalValue = coerceValue(fieldSchema.type, value);
            const { succeeded, failed } = await runBulk(
                files,
                f => FileService.updateFileMetadata(f.id, { [finalKey]: finalValue })
            );
            if (succeeded.length > 0) {
                notify({
                    type: 'success',
                    title: 'Metadata updated',
                    message: `Set "${finalKey}" on ${succeeded.length} file(s).`,
                });
            }
            if (failed.length > 0) {
                reportError(
                    `${failed.length} file(s) could not be updated. Please try again.`,
                    { title: 'Some files could not be updated' }
                );
            }
            onApplied?.();
            onClose?.();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal onClose={onClose}>
            <div className="bulk-modal">
                <div className="bulk-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h2>Set metadata</h2>
                        <p>Applies to {files.length} selected file(s)</p>
                    </div>
                    <ClosingX onClick={onClose} />
                </div>

                <div className="bulk-field">
                    <label htmlFor="bulk-meta-key">Property</label>
                    <select
                        id="bulk-meta-key"
                        value={keyChoice}
                        onChange={e => setKeyChoice(e.target.value)}
                        disabled={busy}
                    >
                        {SCHEMA_KEYS.map(([key, schema]) => (
                            <option key={key} value={key}>{schema.label || key}</option>
                        ))}
                        <option value={CUSTOM_KEY}>Custom property…</option>
                    </select>
                </div>

                {isCustom && (
                    <div className="bulk-field">
                        <label htmlFor="bulk-meta-custom">Property name</label>
                        <input
                            id="bulk-meta-custom"
                            type="text"
                            placeholder="e.g. category"
                            value={customKey}
                            onChange={e => setCustomKey(e.target.value)}
                            disabled={busy}
                        />
                    </div>
                )}

                <div className="bulk-field">
                    <label htmlFor="bulk-meta-value">Value</label>
                    {fieldSchema.options ? (
                        <select
                            id="bulk-meta-value"
                            value={value}
                            onChange={e => setValue(e.target.value)}
                            disabled={busy}
                        >
                            <option value="" disabled>Select {fieldSchema.label}…</option>
                            {fieldSchema.options.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    ) : (
                        <input
                            id="bulk-meta-value"
                            type={fieldSchema.type === 'number' ? 'number' : 'text'}
                            placeholder={isList ? 'Comma-separated values' : 'Enter value…'}
                            value={value}
                            onChange={e => setValue(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && canSubmit && handleApply()}
                            disabled={busy}
                        />
                    )}
                    {isList && <span className="bulk-field-hint">Separate multiple values with commas. This replaces the existing list on each file.</span>}
                </div>

                <div className="bulk-footer">
                    <BButton onClick={onClose}>Cancel</BButton>
                    <BButton className="btn-primary" enabled={canSubmit} onClick={handleApply}>
                        Apply to {files.length}
                    </BButton>
                </div>
            </div>
        </Modal>
    );
};

export default BulkMetadataModal;
